import { Captions, Trash, Upload } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

function useDropActive() {
  const [active, setActive] = React.useState(false);

  const handleDrag = (event: React.DragEvent<HTMLButtonElement>): void => {
    event.preventDefault();
    event.stopPropagation();
    if (event.type === "dragenter" || event.type === "dragover") {
      setActive(true);
    } else if (event.type === "dragleave") {
      setActive(false);
    }
  };

  return { active, setActive, handleDrag };
}

export function EditedSrtDropZone({
  attached,
  label,
  onBrowse,
  onFiles,
  onRemove,
}: {
  attached: boolean;
  label: string;
  onBrowse: () => void;
  onFiles: (files: FileList) => void;
  onRemove: () => void;
}) {
  const { active, setActive, handleDrag } = useDropActive();

  return (
    <div className="relative w-[150px] min-h-[180px] shrink-0 self-stretch">
      {attached ? (
        <button
          type="button"
          className="absolute top-2 right-2 z-10 flex items-center justify-center size-6 rounded-full text-white border-none outline-none transition-colors cursor-pointer shadow-3xs bg-slate-900/70 hover:bg-rose-600"
          onClick={event => {
            event.stopPropagation();
            onRemove();
          }}
          title="Delete SRT"
        >
          <Trash className="size-3.5" />
        </button>
      ) : null}
      <button
        type="button"
        onClick={event => {
          event.stopPropagation();
          onBrowse();
        }}
        onDragEnter={handleDrag}
        onDragOver={handleDrag}
        onDragLeave={handleDrag}
        onDrop={event => {
          event.preventDefault();
          event.stopPropagation();
          setActive(false);
          if (event.dataTransfer.files?.length) {
            onFiles(event.dataTransfer.files);
          }
        }}
        className={cn(
          "flex h-full min-h-[180px] w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-4 text-center cursor-pointer select-none transition-all",
          attached
            ? active
              ? "border-indigo-500 bg-indigo-50/40"
              : "border-indigo-300 bg-indigo-50/40 hover:border-indigo-400"
            : active
            ? "border-indigo-500 bg-indigo-50/30 scale-[0.99]"
            : "border-amber-300 bg-amber-50 hover:border-amber-400"
        )}
      >
        <div
          className={cn(
            "size-10 rounded-full flex items-center justify-center border shadow-3xs",
            attached
              ? "bg-indigo-50 text-indigo-500 border-indigo-100/50"
              : "bg-amber-100 text-amber-700 border-amber-200"
          )}
        >
          <Captions className="size-5" />
        </div>
        <span className={cn("text-xs font-semibold break-all", attached ? "text-slate-700" : "text-amber-900")}>
          {active ? "Drop SRT here" : attached ? label : "Upload SRT"}
        </span>
        {!attached ? <span className="text-[11px] text-amber-800/80">Click or drop</span> : null}
      </button>
    </div>
  );
}

export function EditedVideoDropZone({
  onBrowse,
  onFiles,
}: {
  onBrowse: () => void;
  onFiles: (files: FileList) => void;
}) {
  const { active, setActive, handleDrag } = useDropActive();

  return (
    <button
      type="button"
      onClick={event => {
        event.stopPropagation();
        onBrowse();
      }}
      onDragEnter={handleDrag}
      onDragOver={handleDrag}
      onDragLeave={handleDrag}
      onDrop={event => {
        event.preventDefault();
        event.stopPropagation();
        setActive(false);
        if (event.dataTransfer.files?.length) {
          onFiles(event.dataTransfer.files);
        }
      }}
      className={cn(
        "flex w-[150px] min-h-[180px] shrink-0 self-stretch flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed px-3 py-4 text-center cursor-pointer select-none transition-all",
        active
          ? "border-indigo-500 bg-indigo-50/30 scale-[0.99]"
          : "border-slate-200 bg-white hover:border-indigo-400 hover:bg-indigo-50/10"
      )}
    >
      <div className="size-10 rounded-full flex items-center justify-center border shadow-3xs bg-indigo-50 text-indigo-500 border-indigo-100/50">
        <Upload className="size-5" />
      </div>
      <span className="text-xs font-semibold text-slate-700">{active ? "Drop video here" : "Upload video"}</span>
      <span className="text-[11px] text-slate-400">Click or drop</span>
    </button>
  );
}
